import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {getIsUserGlobalAdmin} from '../../../../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {DropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemExternalLinkIcon} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemExternalLinkIcon';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {LinkNavigator} from '../../../../../../libs/shared/ui/src/components/navigation/navigationContext';
import {useLinkNavigation} from '../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {LocalApplicationFeaturesEnum} from '../../../../../helpers/localApplicationHelpers';
import {ApplicationModel} from '../../../../../models/api/applicationModel';
import {LocalApplicationModel} from '../../../../../models/api/localApplicationModel';
import {SettingsRootPagesEnum} from '../../../../../models/url/models/types';
import {buildSettingsDevelopersPath} from '../../../../../models/url/settingsUrlHelpers';
import {SettingsDevelopersPagesEnum} from '../../../../../models/url/settingsUrlModel';
import {useFetchLocalApplication} from '../../../../../settings/fetchers/localApplications';
import {makeGetApplicationForTopic, makeGetTopic} from '../../../../../store/data/topics/topicsSelectors';

/*
 * Props.
 */

interface ActivityDynamicObjectPillProps {
  topicId: number;
}

interface RulePillDivProps {
  $isOpen: boolean;
}

/*
 * Style.
 */

const StyledPillDiv = styled.div<RulePillDivProps>`
  padding: 0 4px 1px 3px;
  max-width: 250px;

  background: ${(p) => (p.$isOpen ? p.theme.greys.shade30 : p.theme.greys.shade10)};
  border: 1px solid ${(p) => p.theme.greys.shade60};
  border-radius: 100px;

  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.semibold};
  display: flex;
  align-items: center;
  gap: 4px;

  ${ellipsis()};

  &:hover {
    background: ${(p) => p.theme.greys.shade30};
    color: ${(p) => p.theme.greys.shade70};
  }
`;

const StyledApplicationWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
`;

const StyledApplicationImg = styled.img`
  width: 12px;
  height: 12px;
`;

/*
 * Component.
 */

export const ActivityApplicationForTopicPill: FC<ActivityDynamicObjectPillProps> = (props) => {
  const {topicId} = props;
  const getTopic = useMemo(() => makeGetTopic(() => topicId), [topicId]);
  const topic = useAppSelector(getTopic);
  const getApplication = useMemo(() => makeGetApplicationForTopic(topic), [topic]);
  const application = useAppSelector(getApplication);
  const {application: localApplication} = useFetchLocalApplication(application);
  const isUserAdmin = useAppSelector(getIsUserGlobalAdmin);
  const {navigateToLink} = useLinkNavigation();

  if (!application) {
    return null;
  }

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => renderPill(isOpen, localApplication || application)}
      renderModal={(popoverProps) =>
        renderDropdown(popoverProps, isUserAdmin, navigateToLink, localApplication)
      }
    />
  );
};

function renderPill(isOpen: boolean, application: LocalApplicationModel | ApplicationModel) {
  return (
    <StyledPillDiv $isOpen={isOpen}>
      {renderIcon(application)}
      {application.name}
    </StyledPillDiv>
  );
}

function renderDropdown(
  popoverProps: PopoverTransferProps,
  isUserAdmin: boolean,
  navigateToLink: LinkNavigator,
  application?: LocalApplicationModel,
) {
  return (
    <SimpleDropdown {...popoverProps}>
      {application && (
        <SimpleDropdownItem
          isDisabled={!isUserAdmin}
          onSelect={() => {
            navigateToLink(
              buildSettingsDevelopersPath({
                rootPage: SettingsRootPagesEnum.DEVELOPERS,
                subPage: SettingsDevelopersPagesEnum.APPLICATION_FEATURES,
                applicationId: application.id,
                feature: LocalApplicationFeaturesEnum.CONNECTORS,
              }),
            );
          }}
        >
          <Trans id="activity-application-object-pill-edit">Edit application object configurations</Trans>
        </SimpleDropdownItem>
      )}
      <DropdownItem
        onSelect={() => {
          navigateToLink('https://help.front.com/en/articles/2002');
        }}
      >
        <DropdownItemContent>
          <Trans id="activity-application-object-pill-learnmore">Learn more about application objects</Trans>
        </DropdownItemContent>
        <DropdownItemExternalLinkIcon />
      </DropdownItem>
    </SimpleDropdown>
  );
}

/*
 * Helpers.
 */

function renderIcon(application?: LocalApplicationModel | ApplicationModel) {
  if (application && application.iconUrl) {
    return (
      <StyledApplicationWrapperDiv>
        <StyledApplicationImg src={application.iconUrl} />
      </StyledApplicationWrapperDiv>
    );
  }
  return <Icon name="topicWebSmall" size={12} />;
}
